import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { createProject, projectQueries } from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';

import {
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

export const NewProjectForm = (): JSX.Element => {
  const router = useRouter();
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const { mutateAsync, isPending } = useMutation({
    mutationKey: generateMutationKey(projectQueries.all(), 'create', true),
    mutationFn: async (values: FormValues) => {
      await createProject(values);
    },
    onSuccess: async () => {
      toast({
        title: 'newProject.form.submit.success',
        variant: 'default',
      });
      router.navigate({
        to: '/integrations',
      });
    },
    onError: () => {
      toast({
        title: 'newProject.form.submit.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(values => mutateAsync(values))}>
        <div className="items-center gap-4 py-4 md:max-w-[50%]">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  <FormattedMessage id="newProject.form.name.label" />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    type="text"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button type="submit" variant="main" disabled={isPending}>
          {isPending ? (
            <FormattedMessage id="newProject.form.submit.pending" />
          ) : (
            <FormattedMessage id="newProject.form.submit.label" />
          )}
        </Button>
      </form>
    </Form>
  );
};
